import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CalendarCheck, LogOut, ShieldCheck, LayoutDashboard, User, Sparkles, FileText, Bell } from "lucide-react";
import { toast } from "sonner";

const ACC_LANGS = [
  { code: "", name: "English" }, { code: "hi", name: "हिन्दी" }, { code: "ta", name: "தமிழ்" },
  { code: "te", name: "తెలుగు" }, { code: "bn", name: "বাংলা" }, { code: "mr", name: "मराठी" },
  { code: "gu", name: "ગુજરાતી" }, { code: "kn", name: "ಕನ್ನಡ" },
];

export default function Account() {
  const { user, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [cities, setCities] = useState([]);
  const [prefs, setPrefs] = useState(null);

  useEffect(() => {
    api.get("/bookings/mine").then((r) => setBookings(r.data));
    api.get("/geo/cities").then((r) => setCities(r.data)).catch(() => {});
    api.get("/me/muhurat-prefs").then((r) => setPrefs(r.data)).catch(() => {});
  }, []);
  if (!user) return null;

  const onCity = (v) => {
    const c = cities.find((x) => x.name.toLowerCase() === v.trim().toLowerCase());
    setPrefs({ ...prefs, city_name: v, coordinates: c ? c.coord : prefs?.coordinates, offset: c ? c.offset : prefs?.offset });
  };
  const savePrefs = async () => {
    try { const r = await api.put("/me/muhurat-prefs", prefs); setPrefs(r.data); toast.success("Reminder settings saved"); }
    catch (e) { toast.error("Could not save settings"); }
  };

  const confirmed = bookings.filter((b) => b.status === "confirmed").length;

  return (
    <div className="px-4 sm:px-6 lg:px-12 max-w-5xl mx-auto py-12" data-testid="account-page">
      <div className="rounded-3xl bg-gradient-to-br from-primary to-[#8e2d16] text-primary-foreground p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
        {user.picture ? <img src={user.picture} alt="" className="h-20 w-20 rounded-full object-cover border-2 border-white/30" /> : <span className="h-20 w-20 grid place-items-center rounded-full bg-white/20"><User className="h-9 w-9" /></span>}
        <div className="flex-1">
          <h1 className="font-serif text-2xl sm:text-3xl font-bold">{user.name}</h1>
          <p className="text-primary-foreground/85">{user.email}</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {user.roles?.map((r) => <span key={r} className="text-xs px-2.5 py-1 rounded-full bg-white/20 capitalize">{r}</span>)}
          </div>
        </div>
        <Button onClick={logout} data-testid="logout-button" variant="secondary" className="rounded-full"><LogOut className="mr-2 h-4 w-4" /> Sign out</Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        <div className="rounded-2xl border border-border bg-card p-5"><p className="font-serif text-3xl font-bold text-primary">{bookings.length}</p><p className="text-sm text-muted-foreground">Total Bookings</p></div>
        <div className="rounded-2xl border border-border bg-card p-5"><p className="font-serif text-3xl font-bold text-primary">{confirmed}</p><p className="text-sm text-muted-foreground">Confirmed</p></div>
        <div className="rounded-2xl border border-border bg-card p-5"><p className="font-serif text-3xl font-bold text-primary">{inr(bookings.filter(b=>b.status!=="cancelled").reduce((s, b) => s + b.total, 0))}</p><p className="text-sm text-muted-foreground">Total Offerings</p></div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Link to="/my-bookings" data-testid="account-bookings" className="rounded-2xl border border-border bg-card p-5 hover-lift hover:border-accent"><CalendarCheck className="h-6 w-6 text-accent mb-3" /><p className="font-serif font-semibold">My Bookings</p><p className="text-sm text-muted-foreground">View & manage</p></Link>
        <Link to="/my-reports" data-testid="account-reports" className="rounded-2xl border border-border bg-card p-5 hover-lift hover:border-accent"><FileText className="h-6 w-6 text-accent mb-3" /><p className="font-serif font-semibold">My Reports</p><p className="text-sm text-muted-foreground">Download PDFs</p></Link>
        <Link to="/divine-bless" className="rounded-2xl border border-border bg-card p-5 hover-lift hover:border-accent"><Sparkles className="h-6 w-6 text-accent mb-3" /><p className="font-serif font-semibold">Book Worship</p><p className="text-sm text-muted-foreground">Explore services</p></Link>
        <Link to="/purohit" data-testid="account-purohit" className="rounded-2xl border border-border bg-card p-5 hover-lift hover:border-accent"><LayoutDashboard className="h-6 w-6 text-accent mb-3" /><p className="font-serif font-semibold">Purohit Portal</p><p className="text-sm text-muted-foreground">Register & manage</p></Link>
        {user.roles?.includes("admin") && (
          <Link to="/admin" data-testid="account-admin" className="rounded-2xl border border-border bg-card p-5 hover-lift hover:border-accent"><ShieldCheck className="h-6 w-6 text-accent mb-3" /><p className="font-serif font-semibold">Admin</p><p className="text-sm text-muted-foreground">Verify purohits</p></Link>
        )}
      </div>

      {prefs && (
        <div className="rounded-2xl border border-border bg-card p-6 mt-6" data-testid="muhurat-prefs">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-serif text-lg font-semibold flex items-center gap-2"><Bell className="h-5 w-5 text-accent" /> Daily Muhurat Reminder</h2>
              <p className="text-sm text-muted-foreground mt-1">Get a morning email with tomorrow's Abhijit Muhurat, Amrit Kaal and best Choghadiya for your city.</p>
            </div>
            <button data-testid="muhurat-toggle" onClick={() => setPrefs({ ...prefs, enabled: !prefs.enabled })}
              className={`shrink-0 h-7 w-12 rounded-full transition-colors relative ${prefs.enabled ? "bg-emerald-500" : "bg-muted"}`}>
              <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${prefs.enabled ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">City</label>
              <input list="acc-cities" data-testid="muhurat-city" value={prefs.city_name || ""} onChange={(e) => onCity(e.target.value)} className="w-full h-10 rounded-xl border border-border bg-background px-3 text-sm" placeholder="Start typing a city…" />
              <datalist id="acc-cities">{cities.map((c) => <option key={c.name} value={c.name}>{c.state}</option>)}</datalist>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Email language</label>
              <select data-testid="muhurat-lang" value={prefs.lang || ""} onChange={(e) => setPrefs({ ...prefs, lang: e.target.value })} className="w-full h-10 rounded-xl border border-border bg-background px-3 text-sm">
                {ACC_LANGS.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
              </select>
            </div>
          </div>
          <Button onClick={savePrefs} data-testid="muhurat-save" className="rounded-full mt-4 bg-primary text-primary-foreground">Save reminder settings</Button>
        </div>
      )}
    </div>
  );
}
